/*
 * Font-size FLOOR planning (pure — no Office.js). Decides how to raise any text
 * that renders below a readable minimum (e.g. "nothing smaller than 20pt across
 * the deck") without clobbering runs that are already large enough.
 *
 * A text shape reports ONE size when every run shares it, or `null` when the box
 * mixes sizes (a bold header line above smaller body). In the mixed case we drop
 * to per-paragraph reads; a paragraph that is ITSELF mixed (null) is left alone
 * rather than flattened, so raising the body never shrinks a big header run.
 */

export type SizeRead = number | null;

export interface FloorAction {
  scope: "shape" | "paragraph";
  index?: number; // paragraph index when scope === "paragraph"
  newSize: number;
}

export interface FloorPlan {
  actions: FloorAction[];
  /** Paragraph indices we could not raise safely (mixed sub-runs) — report them. */
  mixedSkipped: number[];
}

function belowFloor(size: SizeRead, floor: number): boolean {
  return typeof size === "number" && size > 0 && size < floor;
}

export function planFontFloor(shapeSize: SizeRead, paragraphSizes: SizeRead[], floor: number): FloorPlan {
  // Uniform box: one decision covers the whole shape.
  if (typeof shapeSize === "number" && shapeSize > 0) {
    return { actions: belowFloor(shapeSize, floor) ? [{ scope: "shape", newSize: floor }] : [], mixedSkipped: [] };
  }
  // Mixed (or unreadable) shape size → fall to per-paragraph.
  const actions: FloorAction[] = [];
  const mixedSkipped: number[] = [];
  for (let i = 0; i < paragraphSizes.length; i++) {
    const s = paragraphSizes[i];
    if (belowFloor(s, floor)) {
      actions.push({ scope: "paragraph", index: i, newSize: floor });
    } else if (s == null) {
      // Paragraph itself mixes sizes — a blanket raise would shrink its large run.
      mixedSkipped.push(i);
    }
  }
  return { actions, mixedSkipped };
}
